import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Box, Inline } from '@atlaskit/primitives/compiled';

import { DynamicLayoutComponent } from '../../component/dynamic-layout-component';

import { useAdaptiveSmartCardVariant } from './hooks/use-adaptive-smart-card-variant';
import type { LayoutProps } from './layout-types';

const styles = cssMap({
	leftColumnStyles: {
		flexGrow: 1,
		flexShrink: 1,
		minWidth: '0px',
	},
	rightColumnStyles: {
		flexShrink: 0,
		minWidth: '0px',
	},
});

const REQUIRED_ELEMENTS_COUNT = 1;

const getElementKey = ({ key, pos, index }: { key?: string; pos?: string; index: number }) => {
	if (key && pos) {
		return `${key}-${pos}-${index}`;
	}
	if (key) {
		return `${key}-${index}`;
	}
	if (pos) {
		return `${pos}-${index}`;
	}
	return index;
};

export const TwoColumnLayout = ({
	elements,
	key,
	isFoldable = false,
}: LayoutProps): React.JSX.Element | null => {
	const {
		textContentRef,
		leftNeedsMeasurement,
		rightNeedsMeasurement,
		leftElements,
		rightElements,
		cardVariant,
	} = useAdaptiveSmartCardVariant(elements);

	if (elements.length < REQUIRED_ELEMENTS_COUNT) {
		return null;
	}

	return (
		<Inline space="space.200" grow="fill">
			<Box
				xcss={styles.leftColumnStyles}
				ref={leftNeedsMeasurement ? textContentRef : undefined}
				testId="two-column-layout-left"
			>
				{leftElements.map((element, index) => (
					<DynamicLayoutComponent
						component={element}
						position="two-columns-left"
						key={getElementKey({ key, pos: element.attributes?.pos, index })}
						isFoldable={isFoldable}
						variant={cardVariant}
					/>
				))}
			</Box>
			<Box
				xcss={styles.rightColumnStyles}
				ref={rightNeedsMeasurement ? textContentRef : undefined}
				testId="two-column-layout-right"
			>
				{rightElements.map((element, index) => (
					<DynamicLayoutComponent
						component={element}
						position="two-columns-right"
						key={getElementKey({ key, pos: element.attributes?.pos, index })}
						isFoldable={isFoldable}
						variant={cardVariant}
					/>
				))}
			</Box>
		</Inline>
	);
};
